import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Badge, BadgeProps } from 'antd-v5';
import { TFunction } from 'i18next';
import {
  ROUTER_ORDER_LIST,
  ROUTER_PROMOTION,
  ROUTER_TIRES,
  ROUTER_UNIVERSAL_PRODUCTS,
  ROUTER_ESTIMATE,
} from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { AtomAltLightIcon, AtomLightIcon, ListCheckIcon, PromoIcon, SheetIcon, WheelIcon } from 'assets/icons';
import {
  getHeaderCountBadgeProps,
  HEADER_ESTIMATE_TEXT_BADGE_OFFSET,
} from 'components/Page/Header/TopHeader/badgeStyles';
import { HEADER_HEIGHT } from 'components/Page/Header/TopHeader/TopHeader';
import { Sdiv } from 'components/Page/Header/TopHeader/TopHeader.styled';
import { getCurrentEstimateId, getEstimateBadgeCount } from 'domains/estimate/Estimate.store';
import { getCurrentPromotionsData } from 'domains/promotion/Promotion.store';
import { getUserCountry } from 'domains/user';
import { CREATE_NEW } from 'pages/BackOfficePage/BackOfficePage.type';
import { theme } from 'styles';
import { Flex, Icon, IconType, Link, MarginBox, Text } from 'UI';
import { getCondArrayItem, useExtraLarge, useExtraMidLarge } from 'utils';
import { Country } from 'utils/i18n/Country';

const R1_MENU_ICON_SIZE = 20;

const r1MenuIcon = (IconComponent: IconType) => (
  <Icon IconComponent={IconComponent} size={R1_MENU_ICON_SIZE} color={theme.color.brand_black} display={'block'} />
);

export type LinkItemsDisplayMode = 'sidebar' | 'top-header';

export const getLinkItems = (
  t: TFunction,
  showTires: boolean,
  showCurrentPromotions: boolean,
  displayMode: LinkItemsDisplayMode,
) => {
  const isTopHeader = displayMode === 'top-header';
  const isSidebar = displayMode === 'sidebar';
  return [
    {
      link: ROUTER_UNIVERSAL_PRODUCTS,
      icon: AtomLightIcon,
      r1MenuIcon: r1MenuIcon(AtomAltLightIcon),
      text: t('catalog.universal_products', 'Universal products'),
      dataCy: 'link-universal-products',
      key: 'link-universal-products',
      children: [],
    },
    ...getCondArrayItem(
      showTires && {
        link: ROUTER_TIRES,
        icon: WheelIcon,
        r1MenuIcon: r1MenuIcon(WheelIcon),
        text: t('catalog.tires', 'Tires'),
        dataCy: 'link-tires',
        key: 'link-tires',
        children: [],
      },
    ),
    ...getCondArrayItem(
      showCurrentPromotions && {
        link: ROUTER_PROMOTION,
        icon: PromoIcon,
        r1MenuIcon: r1MenuIcon(PromoIcon),
        text: t('promotions.title', 'Promotions'),
        dataCy: 'link-promotions',
        key: 'link-promotions',
        children: [],
      },
    ),
    ...getCondArrayItem(
      isTopHeader && {
        link: ROUTER_ESTIMATE,
        icon: SheetIcon,
        text: t('settings.estimates', 'Estimates'),
        dataCy: 'link-estimate',
        key: 'link-estimate',
      },
    ),
    ...getCondArrayItem(
      isSidebar && {
        link: `${ROUTER_ORDER_LIST}/${CREATE_NEW}`,
        icon: ListCheckIcon,
        r1MenuIcon: r1MenuIcon(ListCheckIcon),
        text: t('catalog.order_by_reference', 'Order by reference'),
        dataCy: 'link-order-by-reference',
        key: 'link-order-by-reference',
        children: [],
      },
    ),
  ];
};

const LinkTabs = () => {
  const { t } = useTranslation();
  const country = useSelector(getUserCountry);
  const showTires = Country.canShowTiresByCountry(country);
  const promotionsData = useSelector(getCurrentPromotionsData);
  const showCurrentPromotions = promotionsData?.length > 0;
  const linkItems = getLinkItems(t, showTires, showCurrentPromotions, 'top-header');
  const currentEstimateId = useSelector(getCurrentEstimateId);
  const estimateItemsCount = useSelector((state: RootState) => getEstimateBadgeCount(state, currentEstimateId));
  const [hoveredIcon, setHoveredIcon] = useState<string | undefined>(undefined);
  const isXl = useExtraLarge();
  const isXml = useExtraMidLarge();
  const showIconsMode = !isXml && (!isXl || linkItems.length > 3);

  const renderEstimateCountBadge = (content: React.ReactNode, offset?: BadgeProps['offset']) => {
    if (estimateItemsCount <= 0) {
      return content;
    }

    return <Badge {...getHeaderCountBadgeProps(estimateItemsCount, { offset })}>{content}</Badge>;
  };

  return (
    <Flex minWidth={250}>
      {linkItems.map((item) => {
        const isHoveredIcon = hoveredIcon === item.link;
        const isEstimateLink = item.link === ROUTER_ESTIMATE;
        return (
          <Link to={item.link} key={item.link} data-cy={`top-link-${item.text.toLowerCase().replaceAll(/\s/g, '-')}`}>
            <Sdiv
              onMouseEnter={() => setHoveredIcon(item.link)}
              onMouseLeave={() => setHoveredIcon(undefined)}
              isHovered={isHoveredIcon}
            >
              <MarginBox mx={7}>
                <Flex align={'center'} gap={10} wrap={'nowrap'} minHeight={HEADER_HEIGHT} maxHeight={HEADER_HEIGHT}>
                  {showIconsMode &&
                    !isHoveredIcon &&
                    (isEstimateLink ? (
                      renderEstimateCountBadge(
                        <Icon IconComponent={item.icon} size={26} color={theme.color.white} display={'block'} />,
                      )
                    ) : (
                      <Icon IconComponent={item.icon} size={26} color={theme.color.white} display={'block'} />
                    ))}
                  {(!showIconsMode || isHoveredIcon) &&
                    (isEstimateLink ? (
                      renderEstimateCountBadge(
                        <Text type="base" color="white" noWrap disableGutter>
                          {item.text}
                        </Text>,
                        HEADER_ESTIMATE_TEXT_BADGE_OFFSET,
                      )
                    ) : (
                      <Text type="base" color="white" noWrap disableGutter>
                        {item.text}
                      </Text>
                    ))}
                </Flex>
              </MarginBox>
            </Sdiv>
          </Link>
        );
      })}
    </Flex>
  );
};

export default LinkTabs;
